/**
 * KontrollListeScreen — the per-stage Kontrollliste (Bereich 5, route
 * /portfolio/kontrolle/$stufe). Reached by clicking a stage tile on the Portfolio panel.
 *
 * "‹ Zurück zum Dashboard" · title "Kontrollliste · Stufe N: {name}" + "≥ N Tage" badge ·
 * count line · a Kanal/Kunde/Team filter bar · the table (KANAL · ABSENDER · BETREFF ·
 * KUNDE / POLICE · ALTER · SIGNALE). A row opens the detail. Gated on
 * administration.kontrolle.rueckstand.einsehen (api is the hard line).
 *
 * [Source: V4.0-inbox/cockpit-screenshots/image2,4,7,10.png]
 */

import { useMemo, useState } from 'react';
import { ChevronLeft } from 'lucide-react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { EntityLink } from '../../components/ui/EntityLink';
import { useEffectivePermissions } from '../administration/useEffectivePermissions';
import { useKundenteams } from '../administration/hooks';
import { NoAccess, LoadingState, ErrorState } from '../administration/components';
import {
  useKontrolleRueckstand,
  useKontrolleUebersicht,
  type KontrolleListeFilters,
} from './hooks';
import { KanalBadge } from './components/KanalBadge';
import { SignalBadge } from './components/SignalBadge';
import { SchwelleBadge } from './components/StufePill';
import {
  STUFE_CLASSES,
  STUFE_NAME,
  STUFE_DEFAULT_TAGE,
  KANAL_LABEL,
  KONTROLLE_EINSEHEN_KEY,
  parseStufe,
} from './constants';
import type { KontrolleKanal } from './types';

const KANAL_ORDER: KontrolleKanal[] = ['email', 'postscan', 'portal', 'versicherer_api'];

export function KontrollListeScreen() {
  const { stufe: stufeParam } = useParams({ strict: false }) as { stufe?: string };
  const stufe = parseStufe(stufeParam);
  const navigate = useNavigate();
  const access = useEffectivePermissions();
  const canView = access.can(KONTROLLE_EINSEHEN_KEY);

  const [kanal, setKanal] = useState('');
  const [kunde, setKunde] = useState('');
  const [team, setTeam] = useState('');

  const filters: KontrolleListeFilters = useMemo(
    () => ({ ...(kanal ? { kanal } : {}), ...(kunde ? { kunde } : {}), ...(team ? { team } : {}) }),
    [kanal, kunde, team],
  );

  // "zustaendiges Team" (Kap 4) = the customer's Bereich-2 Kundenteam, whose id the API filters on
  // customer_team_scope.team_id. (Was wrongly filled with Funktionsgruppen -> the filter never matched.)
  const teamsQuery = useKundenteams();
  const teams = teamsQuery.data?.teams ?? [];
  // The tenant-configured Fristen (same source as the dashboard tile) drive the "≥ N Tage" badge.
  const uebersicht = useKontrolleUebersicht(canView);
  const list = useKontrolleRueckstand(stufe ?? 1, filters, canView && stufe != null);
  const rows = list.data ?? [];

  function backToDashboard() {
    void navigate({ to: '/portfolio' });
  }

  if (stufe == null) {
    return (
      <div className="px-6 py-5">
        <button
          type="button"
          onClick={backToDashboard}
          className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft className="size-4" /> Zurück zum Dashboard
        </button>
        <p className="text-sm text-muted-foreground">Diese Stufe existiert nicht.</p>
      </div>
    );
  }

  if (!canView) {
    return (
      <div className="px-6 py-5">
        <NoAccess message="Für den Kontrollbereich fehlt Ihnen die Berechtigung." />
      </div>
    );
  }

  const c = STUFE_CLASSES[stufe];
  // The per-broker configured threshold for this stage (falls back to the seeded default until loaded).
  const schwelleTage =
    uebersicht.data?.stufen.find((s) => s.stufe === stufe)?.schwelle_tage ?? STUFE_DEFAULT_TAGE[stufe];

  return (
    <div className="px-6 py-5">
      <button
        type="button"
        onClick={backToDashboard}
        className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ChevronLeft className="size-4" /> Zurück zum Dashboard
      </button>

      {/* Title + threshold badge */}
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-2xl font-semibold text-foreground">
          Kontrollliste · Stufe {stufe}: {STUFE_NAME[stufe]}
        </h1>
        <SchwelleBadge stufe={stufe} schwelleTage={schwelleTage} />
      </div>
      <p className="mt-1 text-sm text-muted-foreground">
        {rows.length} {rows.length === 1 ? 'Element' : 'Elemente'} in dieser Stufe · sortiert nach
        Alter (älteste zuerst)
      </p>

      {/* Filter bar (Kanal / Kunde / Team) */}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <select
          aria-label="Kanal filtern"
          value={kanal}
          onChange={(e) => setKanal(e.target.value)}
          className="h-8 rounded-md border border-border bg-background px-2 text-xs"
        >
          <option value="">Alle Kanäle</option>
          {KANAL_ORDER.map((k) => (
            <option key={k} value={k}>
              {KANAL_LABEL[k]}
            </option>
          ))}
        </select>
        <input
          type="search"
          aria-label="Kunde filtern"
          placeholder="Kunde suchen …"
          value={kunde}
          onChange={(e) => setKunde(e.target.value)}
          className="h-8 w-48 rounded-md border border-border bg-background px-2 text-xs"
        />
        <select
          aria-label="Team filtern"
          value={team}
          onChange={(e) => setTeam(e.target.value)}
          className="h-8 rounded-md border border-border bg-background px-2 text-xs"
        >
          <option value="">Alle Teams</option>
          {teams.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
      </div>

      {/* Table */}
      {list.isLoading ? (
        <div className="mt-4">
          <LoadingState />
        </div>
      ) : list.isError ? (
        <div className="mt-4">
          <ErrorState />
        </div>
      ) : (
        <div className="mt-4 overflow-x-auto rounded-xl border border-border bg-card">
          <table className="w-full min-w-[820px] text-sm">
            <thead>
              <tr className="border-b border-border text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                <th className="px-4 py-2.5 font-medium">Kanal</th>
                <th className="px-4 py-2.5 font-medium">Absender</th>
                <th className="px-4 py-2.5 font-medium">Betreff</th>
                <th className="px-4 py-2.5 font-medium">Kunde / Police</th>
                <th className="px-4 py-2.5 font-medium">Alter</th>
                <th className="px-4 py-2.5 font-medium">Signale</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-sm text-muted-foreground">
                    Keine Elemente in dieser Stufe.
                  </td>
                </tr>
              ) : (
                rows.map((row) => {
                  const link = row.police_bezeichnung ?? row.kunde_name;
                  return (
                    <tr
                      key={row.id}
                      onClick={() =>
                        void navigate({
                          to: '/portfolio/kontrolle/$stufe/$id',
                          params: { stufe: String(stufe), id: row.id },
                        })
                      }
                      className="cursor-pointer border-b border-border/60 last:border-0 hover:bg-secondary/50"
                    >
                      <td className="px-4 py-3">
                        <KanalBadge kanal={row.kanal} />
                      </td>
                      <td className="px-4 py-3 font-semibold text-foreground">{row.absender}</td>
                      <td className="px-4 py-3 text-foreground">{row.betreff}</td>
                      <td className="px-4 py-3">
                        {link == null ? (
                          <span className="text-muted-foreground">-</span>
                        ) : row.subject_id ? (
                          <EntityLink
                            onActivate={() =>
                              void navigate({ to: '/kunden/$id', params: { id: row.subject_id! } })
                            }
                          >
                            {link}
                          </EntityLink>
                        ) : (
                          <span className="text-muted-foreground">{link}</span>
                        )}
                      </td>
                      <td className={`px-4 py-3 font-semibold tabular-nums ${c.text}`}>
                        {row.alter_tage} T.
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1.5">
                          {row.signale.map((s) => (
                            <SignalBadge key={s.typ} typ={s.typ} />
                          ))}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
